import React, { useState } from 'react'
import { useLocation, Link, useNavigate } from 'react-router-dom'
import {
  HomeIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  CubeIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  ArrowsRightLeftIcon,
  DocumentIcon,
  ChartBarIcon,
  TagIcon,
  UsersIcon,
  DocumentChartBarIcon,
  LinkIcon,
  SparklesIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentArrowUpIcon,
  ClipboardDocumentCheckIcon,
  CalculatorIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  BeakerIcon,
  CogIcon
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/stores/authStore'

interface SidebarProps {
  isCollapsed?: boolean
}

const menuSections = [
  {
    title: 'Dashboard',
    items: [
      { icon: HomeIcon, label: 'Visão Geral', path: '/' }
    ]
  },
  {
    title: 'Vendas & Produtos',
    color: 'blue',
    items: [
      { icon: ShoppingCartIcon, label: 'Nova Venda', path: '/vendas' },
      { icon: DocumentTextIcon, label: 'Contas a Receber', path: '/financeiro/contas-receber' },
      { icon: CubeIcon, label: 'Produtos & Servicos', path: '/produtos' },
      { icon: UsersIcon, label: 'Equipe de Vendas', path: '/sales/sellers' },
      { icon: CurrencyDollarIcon, label: 'Comissoes', path: '/sales/commissions' },
      { icon: UsersIcon, label: 'Clientes (CRM)', path: '/crm/customers' },
      { icon: ChartBarIcon, label: 'Analytics CRM', path: '/crm/analytics' }
    ]
  },
  {
    title: 'Financeiro',
    items: [
      { icon: BuildingLibraryIcon, label: 'Contas Bancárias', path: '/accounts' },
      { icon: ArrowsRightLeftIcon, label: 'Transações', path: '/financeiro/transferencias' },
      { icon: DocumentIcon, label: 'Contas a Pagar', path: '/bills' },
      { icon: DocumentTextIcon, label: 'OCR Notas Fiscais', path: '/invoices/ocr' },
      { icon: ChartBarIcon, label: 'Fluxo de Caixa', path: '/cash-flow' },
      { icon: TagIcon, label: 'Categorias', path: '/categories' },
      { icon: CurrencyDollarIcon, label: 'Portfolio Crypto', path: '/crypto' }
    ]
  },
  {
    title: 'Contabilidade',
    items: [
      { icon: CalculatorIcon, label: 'Plano de Contas', path: '/accounting' },
      { icon: DocumentChartBarIcon, label: 'Relatórios Gerais', path: '/reports' },
      { icon: TagIcon, label: 'Centros de Custo', path: '/cost-centers' },
      { icon: ChartBarIcon, label: 'Projetos', path: '/projects' }
    ]
  },
  {
    title: 'Importação & Dados',
    items: [
      { icon: DocumentArrowUpIcon, label: 'Importar OFX', path: '/imports/ofx-upload' },
      { icon: ClipboardDocumentCheckIcon, label: 'Classificar Extratos', path: '/bank-statements' }
    ]
  },
  {
    title: 'Integrações',
    items: [
      { icon: LinkIcon, label: 'Gerenciar Integrações', path: '/integrations' },
      { icon: LinkIcon, label: 'Open Banking', path: '/integrations/pluggy' },
      { icon: BanknotesIcon, label: 'Gateway de Pagamento', path: '/payments/gateway-finpro' }
    ]
  },
  {
    title: 'IA & Automação',
    color: 'purple',
    items: [
      { icon: SparklesIcon, label: 'Insights Financeiros', path: '/ai' },
      { icon: ChartBarIcon, label: 'Predições', path: '/ai/predictions' },
      { icon: BeakerIcon, label: 'AI Playground', path: '/ai/playground' },
      { icon: ChatBubbleLeftRightIcon, label: 'Assistente IA', path: '/chatbot' },
      { icon: ArrowsRightLeftIcon, label: 'Conciliação IA', path: '/reconciliation/smart' }
    ]
  },
  {
    title: 'Configurações',
    items: [
      { icon: UsersIcon, label: 'Usuários', path: '/settings/users' },
      { icon: CogIcon, label: 'Geral', path: '/settings' }
    ]
  }
]

export const Sidebar: React.FC<SidebarProps> = ({ isCollapsed = false }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout, user } = useAuthStore()
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'Dashboard': true,
    'Vendas & Produtos': true,
    'Financeiro': true,
    'Contabilidade': false,
    'Importação & Dados': false,
    'Integrações': false,
    'IA & Automação': false,
    'Configurações': false
  })

  const toggleSection = (title: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [title]: !prev[title]
    }))
  }

  const handleLogout = () => {
    // Limpar estado do Zustand
    logout()

    // Limpar TUDO do localStorage para evitar cache
    localStorage.clear()

    // Redirecionar para landing page com reload para limpar cache do React
    navigate('/landing', { replace: true })

    // Force reload para limpar completamente o estado
    window.location.href = '/landing'
  }

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/dashboard'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <div className={`
      bg-white
      text-gray-900 h-screen transition-all duration-300 ease-in-out
      ${isCollapsed ? 'w-16' : 'w-64'}
      border-r border-gray-200 shadow-sm
      flex flex-col
    `}>
      {/* Logo */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg">
            <BanknotesIcon className="w-6 h-6 text-white" />
          </div>
          {!isCollapsed && (
            <div>
              <h1 className="text-xl font-bold text-gray-900">FinPro</h1>
              <p className="text-xs text-gray-500">Gestão Financeira</p>
            </div>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="space-y-1 px-3">
          {menuSections.map((section, sectionIndex) => {
            const isExpanded = expandedSections[section.title] ?? true
            const colorClass = section.color === 'blue'
              ? 'bg-blue-50 hover:bg-blue-100 text-blue-700'
              : section.color === 'purple'
              ? 'bg-purple-50 hover:bg-purple-100 text-purple-700'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'

            return (
              <div key={sectionIndex} className="mb-2">
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.title)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all group font-semibold ${colorClass}`}
                >
                  <span className="text-xs font-semibold uppercase tracking-wide">
                    {section.title}
                  </span>
                  {!isCollapsed && (
                    isExpanded ?
                      <ChevronDownIcon className="w-4 h-4" /> :
                      <ChevronRightIcon className="w-4 h-4" />
                  )}
                </button>

                {/* Section Items */}
                {isExpanded && (
                  <ul className="mt-1 space-y-0.5">
                    {section.items.map((item, index) => {
                      const itemActive = isActive(item.path)

                      return (
                        <li key={index}>
                          <Link
                            to={item.path}
                            className={`
                              flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all group
                              ${itemActive
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-gray-700 hover:bg-gray-100'
                              }
                            `}
                          >
                            <item.icon className={`w-5 h-5 ${itemActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`} />
                            {!isCollapsed && (
                              <div className="flex-1 flex items-center justify-between">
                                <span className="text-sm font-medium">{item.label}</span>
                                {item.badge && !itemActive && (
                                  <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
                                    {item.badge}
                                  </span>
                                )}
                              </div>
                            )}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            )
          })}
        </div>
      </nav>

      {/* User Profile */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center shadow">
            <span className="text-sm font-bold text-white">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </span>
          </div>
          {!isCollapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {user?.name || 'Usuário'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email || 'usuario@email.com'}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Sair"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
